import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, DialogContent, Paper, Typography } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const DEFAULT_FB_SDK_VERSION = "v25.0";

const getFacebookSdkCandidates = () => {
  const backend = String(process.env.REACT_APP_BACKEND_URL || "")
    .trim()
    .replace(/\/$/, "");
  const candidates = [];
  // 1) Proxy pela API — evita Tracking Protection / CORS em connect.facebook.net
  if (backend) {
    candidates.push(`${backend}/integrations/meta/facebook-sdk.js`);
  }
  // 2) Fallback CDN oficial (en_US; nunca pt_BR)
  candidates.push("https://connect.facebook.net/en_US/sdk.js");
  return candidates;
};

const removeFailedFacebookScripts = () => {
  document
    .querySelectorAll("script#facebook-jssdk, script[src*='connect.facebook.net']")
    .forEach(node => {
      node.parentNode?.removeChild(node);
    });
};

const loadFacebookSdk = (appId, graphApiVersion) =>
  new Promise((resolve, reject) => {
    const version = graphApiVersion || DEFAULT_FB_SDK_VERSION;
    let settled = false;

    const finish = fb => {
      if (settled) return;
      settled = true;
      resolve(fb);
    };

    const fail = message => {
      if (settled) return;
      settled = true;
      reject(new Error(message));
    };

    const initSdk = () => {
      try {
        if (!window.FB) {
          fail("SDK da Meta carregou sem expor window.FB.");
          return;
        }
        window.FB.init({
          appId: String(appId),
          cookie: true,
          xfbml: true,
          version,
        });
        finish(window.FB);
      } catch (err) {
        fail(err?.message || "Falha ao inicializar o SDK da Meta.");
      }
    };

    if (window.FB) {
      initSdk();
      return;
    }

    const previousAsyncInit = window.fbAsyncInit;
    window.fbAsyncInit = function fbAsyncInit() {
      if (typeof previousAsyncInit === "function") {
        try {
          previousAsyncInit();
        } catch {
          // ignora falha de init anterior
        }
      }
      initSdk();
    };

    // Remove tentativas antigas (pt_BR / CORS / Tracking) que ficam com id facebook-jssdk
    // e impedem um novo carregamento.
    removeFailedFacebookScripts();

    const candidates = getFacebookSdkCandidates();
    let index = 0;

    const tryNext = () => {
      if (settled) return;
      if (index >= candidates.length) {
        fail(
          "SDK da Meta bloqueado (CORS/Tracking). Use outro navegador ou desative proteção contra rastreamento neste site."
        );
        return;
      }

      const src = candidates[index];
      index += 1;

      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.async = true;
      script.defer = true;
      // Nunca usar crossOrigin — força CORS e quebra o SDK.
      script.src = src;
      script.onload = () => {
        if (window.FB) {
          initSdk();
          return;
        }
        setTimeout(() => {
          if (window.FB) {
            initSdk();
          } else {
            removeFailedFacebookScripts();
            tryNext();
          }
        }, 800);
      };
      script.onerror = () => {
        removeFailedFacebookScripts();
        tryNext();
      };

      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.body.appendChild(script);
      }
    };

    tryNext();
  });

const asObject = value => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (typeof value === "object") return value;
  return {};
};

const extractSignupData = payload => {
  const roots = [
    payload,
    payload?.data,
    payload?.payload,
    asObject(payload?.data),
    asObject(payload?.payload),
    asObject(asObject(payload?.data)?.data),
  ];

  let phoneNumberId = "";
  let wabaId = "";
  let businessId = "";

  roots.forEach(root => {
    const obj = asObject(root);
    phoneNumberId =
      phoneNumberId ||
      obj.phone_number_id ||
      obj.phoneNumberId ||
      obj.phone_number?.id ||
      "";
    wabaId =
      wabaId ||
      obj.waba_id ||
      obj.wabaId ||
      obj.whatsapp_business_account_id ||
      obj.waba?.id ||
      "";
    businessId =
      businessId ||
      obj.business_id ||
      obj.businessId ||
      obj.business?.id ||
      "";
  });

  return { phoneNumberId, wabaId, businessId };
};

const COEX_FINISH_EVENTS = new Set([
  "FINISH",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
  "FINISH_ONLY_WABA",
]);

/**
 * Extras oficiais do Coex (Meta).
 * A opção "conectar WhatsApp Business App" NÃO fica no dropdown de telefone —
 * ela substitui a tela de seleção de WABA. Se essa tela não aparecer, o
 * Config ID / app ainda não tem Coexistência habilitada no painel Meta.
 *
 * @see https://developers.facebook.com/docs/whatsapp/embedded-signup/custom-flows/onboarding-business-app-users/
 */
const buildCoexLoginOptions = config => {
  const mode = String(config?.embeddedSignupVersion || "coex")
    .trim()
    .toLowerCase();

  // Modo oficial Coex (documentação Meta) — sem "version" forçado.
  let extras = {
    setup: {},
    featureType: "whatsapp_business_app_onboarding",
    sessionInfoVersion: "3",
  };

  if (mode === "v4" || mode === "v4-public-preview") {
    // v4 + seletor Coex (parceiros reportam que featureType ainda é necessário)
    extras = {
      setup: {},
      featureType: "whatsapp_business_app_onboarding",
      version: mode === "v4-public-preview" ? "v4-public-preview" : "v4",
    };
  } else if (mode === "v3" || mode === "v2") {
    extras = {
      setup: {},
      featureType: "whatsapp_business_app_onboarding",
      sessionInfoVersion: "3",
      version: mode,
    };
  }

  if (config?.solutionId) {
    extras.setup = {
      ...(extras.setup || {}),
      solutionID: String(config.solutionId),
    };
  }

  return {
    config_id: String(config.configId),
    response_type: "code",
    override_default_response_type: true,
    extras,
  };
};

const MetaCoexSignup = ({
  whatsAppId,
  open = true,
  autoLaunch = false,
  onConnected,
  onClose,
  showInlineButton = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const signupDataRef = useRef({
    phoneNumberId: "",
    wabaId: "",
    businessId: "",
    code: "",
    finished: false,
  });
  const launchedRef = useRef(false);
  const completingRef = useRef(false);
  const completeTimerRef = useRef(null);

  const clearCompleteTimer = useCallback(() => {
    if (completeTimerRef.current) {
      clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
  }, []);

  const completeSignup = useCallback(
    async code => {
      if (!whatsAppId) {
        toast.error("Salve a conexão antes de iniciar o cadastro Coex.");
        setLoading(false);
        return;
      }

      if (completingRef.current) return;
      completingRef.current = true;
      clearCompleteTimer();

      let { phoneNumberId, wabaId } = signupDataRef.current;

      // Coex costuma enviar só waba_id no postMessage; aguarda um pouco se ainda não chegou.
      if (!wabaId && !phoneNumberId) {
        for (let i = 0; i < 25; i += 1) {
          await new Promise(resolve => setTimeout(resolve, 400));
          phoneNumberId = signupDataRef.current.phoneNumberId;
          wabaId = signupDataRef.current.wabaId;
          if (wabaId || phoneNumberId) break;
        }
      }

      setLoading(true);
      try {
        await api.post(`/whatsapp/${whatsAppId}/coex-complete`, {
          code,
          phoneNumberId: phoneNumberId || undefined,
          wabaId: wabaId || undefined,
        });
        toast.success("WhatsApp oficial conectado com sucesso!");
        if (onConnected) onConnected();
        if (onClose) onClose();
      } catch (err) {
        completingRef.current = false;
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [clearCompleteTimer, onClose, onConnected, whatsAppId]
  );

  const scheduleComplete = useCallback(
    (code, delayMs = 2500) => {
      if (!code || completingRef.current) return;
      clearCompleteTimer();
      completeTimerRef.current = setTimeout(() => {
        if (!completingRef.current && signupDataRef.current.code) {
          completeSignup(signupDataRef.current.code);
        }
      }, delayMs);
    },
    [clearCompleteTimer, completeSignup]
  );

  useEffect(() => {
    if (!open) return undefined;

    const fetchConfig = async () => {
      try {
        const { data } = await api.get("/integrations/meta/coex-config");
        if (!data?.appId || !data?.configId) {
          toast.error("Configuração Coex incompleta (appId/configId).");
          return;
        }
        setConfig(data);
        await loadFacebookSdk(data.appId, data.graphApiVersion);
        setSdkReady(true);
      } catch (err) {
        toastError(err);
      }
    };

    fetchConfig();

    return () => {
      clearCompleteTimer();
    };
  }, [clearCompleteTimer, open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleMessage = event => {
      if (
        !event.origin.endsWith("facebook.com") &&
        !event.origin.endsWith("web.facebook.com")
      ) {
        return;
      }

      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type !== "WA_EMBEDDED_SIGNUP") return;

        const extracted = extractSignupData(data);
        if (extracted.phoneNumberId) {
          signupDataRef.current.phoneNumberId = extracted.phoneNumberId;
        }
        if (extracted.wabaId) {
          signupDataRef.current.wabaId = extracted.wabaId;
        }
        if (extracted.businessId) {
          signupDataRef.current.businessId = extracted.businessId;
        }

        if (COEX_FINISH_EVENTS.has(data.event)) {
          signupDataRef.current.finished = true;
          if (signupDataRef.current.code) {
            completeSignup(signupDataRef.current.code);
          }
        }
      } catch {
        // Ignora mensagens que não são JSON do Embedded Signup.
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [open, completeSignup]);

  const handleLaunchSignup = useCallback(() => {
    if (!whatsAppId) {
      toast.error("Salve a conexão antes de iniciar o cadastro Coex.");
      return;
    }

    if (!config || !window.FB || !sdkReady) {
      toast.error("SDK da Meta ainda não carregou. Aguarde alguns segundos.");
      return;
    }

    setLoading(true);
    completingRef.current = false;
    clearCompleteTimer();
    signupDataRef.current = {
      phoneNumberId: "",
      wabaId: "",
      businessId: "",
      code: "",
      finished: false,
    };

    const loginOptions = buildCoexLoginOptions(config);
    // Ajuda a validar no DevTools se featureType/version estão indo no FB.login.
    // eslint-disable-next-line no-console
    console.info("[Coex] FB.login options", JSON.stringify(loginOptions));

    // Callback NÃO pode ser async — o SDK da Meta exige Function, não AsyncFunction.
    window.FB.login(function onFacebookLogin(response) {
      // Mantém o formato oficial Coex: code + extras.featureType.
      if (response?.authResponse?.code) {
        const code = response.authResponse.code;
        signupDataRef.current.code = code;
        toast.info("Finalizando autorização Coex da Meta...");

        // Preferência: concluir no postMessage FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING.
        // Fallback: se o evento não chegar, conclui após alguns segundos.
        if (signupDataRef.current.finished) {
          completeSignup(code);
        } else {
          scheduleComplete(code, 3000);
        }
        return;
      }

      setLoading(false);
      if (response?.status && response.status !== "unknown") {
        toast.error("Cadastro incorporado cancelado ou não concluído.");
      }
    }, loginOptions);
  }, [
    clearCompleteTimer,
    completeSignup,
    config,
    scheduleComplete,
    sdkReady,
    whatsAppId,
  ]);

  useEffect(() => {
    if (!open || !autoLaunch || !sdkReady || !whatsAppId || launchedRef.current) {
      return;
    }
    launchedRef.current = true;
    handleLaunchSignup();
  }, [autoLaunch, handleLaunchSignup, open, sdkReady, whatsAppId]);

  useEffect(() => {
    if (!open) {
      launchedRef.current = false;
      completingRef.current = false;
      clearCompleteTimer();
    }
  }, [clearCompleteTimer, open]);

  const content = (
    <Paper elevation={0} style={{ padding: 16 }}>
      <Typography variant="body1" gutterBottom>
        Conecte o número oficial via Coexistência (Coex).
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        A opção Coex <strong>não</strong> aparece no dropdown &quot;Telefone&quot;
        (novo / virtual / registrado). Ela substitui a tela de WABA por algo como
        &quot;Conectar sua conta do WhatsApp Business App&quot;.
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Se essa tela não aparecer, o problema é no painel Meta (Config ID / permissão
        de Coexistência), não no código do FB.login. Confira o checklist abaixo.
      </Typography>
      <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 8 }}>
        Checklist Meta (app LifeSipCoex):
        <ul style={{ margin: "8px 0 0 16px", padding: 0 }}>
          <li>App é Tech Provider ou Solution Partner</li>
          <li>
            Facebook Login for Business → Configuration com variação{" "}
            <em>WhatsApp Embedded Signup</em>
          </li>
          <li>Novo Config ID dedicado ao Coex (não reutilizar config só Cloud)</li>
          <li>
            Domínio do frontend em Allowed Domains e Valid OAuth Redirect URIs
          </li>
          <li>
            Se ainda faltar, abrir ticket Meta: &quot;Embedded Signup - Coexistence
            Onboarding&quot;
          </li>
        </ul>
      </Typography>
      {config?.configId && (
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          Config ID atual: {config.configId}
          {config.embeddedSignupVersion ? ` · modo ${config.embeddedSignupVersion}` : ""}
        </Typography>
      )}
      {showInlineButton && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleLaunchSignup}
          disabled={loading || !config || !sdkReady || !whatsAppId}
          style={{ marginTop: 12 }}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "Abrir leitura do QR (Coex / WABA)"
          )}
        </Button>
      )}
      {loading && !showInlineButton && (
        <CircularProgress size={28} style={{ marginTop: 16 }} />
      )}
    </Paper>
  );

  if (onClose) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>{content}</DialogContent>
      </Dialog>
    );
  }

  if (!open) return null;
  return content;
};

export default MetaCoexSignup;
